import React from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  InlineStatus,
  InlineStatusAppearance,
  InlineStatusIconType,
  Loader,
  LoaderSizes,
  Pagination,
  SearchInput,
  Tag,
  TagAppearance,
} from "@hid-galaxy-ui/galaxy-react";
import { useLocation, useNavigate, Outlet } from "react-router";
import {
  getAllPartnersSagaAction,
  getFilteredPartnersSagaAction,
} from "../../../../../redux/saga/partner-details/saga-actions";
import { getPartnerTierListSagaAction } from "../../../../../redux/saga/partner-program/saga-actions";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import moment from "moment";
import { PartnerDetailType } from "../../../../../redux/slices/partner-details/Types";
import PartnerFilterModal from "./partner-filter-modal";
import ExportReportModal from "./export-report-modal";
import PartnerColumnsDropdown from "./PartnerColumnsDropdown";
import {
  DEFAULT_VISIBLE_PARTNER_COLUMNS,
  formatPartnerColumnLabel,
  getAvailablePartnerColumns,
  getPartnerCellValue,
  isPartnerColumnKey,
  PartnerColumnKey,
} from "./partnerListColumns";
import { buildPartnerTierNameById } from "./partnerTierFilterUtils";
import {
  useSessionCanAccessAnyModule,
  useSessionCanAccessModule,
  useSessionCanEditModule,
  useSessionModuleReadOnly,
} from "../../../../../hooks/useSessionPermissions";
import {
  PARTNER_MANAGEMENT_PERMISSION_KEYS as PM_KEYS,
  PARTNER_MANAGE_PARTNER_SUBMENU_KEYS,
} from "../../../../../utils/permissions/partnerManagementPermissionKeys";
import IntegrationStatusTab from "./integration-status/IntegrationStatusTab";
import BenefitActivityLogTab from "./benefit-activity-log/BenefitActivityLogTab";
import { PARTNER_PROGRAM_BENEFITS_LOG_TAB } from "../manage-partner/partner-program/partnerProgramTabs";
import { KPI_TILE_COLORS } from "./kpiTileColors";
import CreateNewPartnerModal from "../../../create-new-partner/create-new-partner-modal";
import "./PartnerList.scss";

const PartnerList = () => {
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(20);
  const [filterModalOpen, setFilterModalOpen] = React.useState<boolean>(false);
  const [exportModalOpen, setExportModalOpen] = React.useState<boolean>(false);
  const [createPartnerModalOpen, setCreatePartnerModalOpen] =
    React.useState<boolean>(false);
  const [activeTab, setActiveTab] = React.useState<
    "organizations" | "integration" | "benefits"
  >("organizations");
  const [visibleColumns, setVisibleColumns] = React.useState<
    PartnerColumnKey[]
  >([...DEFAULT_VISIBLE_PARTNER_COLUMNS]);
  const [partnerFilter, setPartnerFilter] = React.useState<{
    partnerName: string[];
    status: string[];
    type: string[];
    tier: string[];
    vertical: string[];
    revenueStream: string[];
    accountManager: string[];
    region: string[];
    country: string[];
    contractName: string[];
    contractStatus: string[];
    supportLevel: string[];
    startDateRange: string[];
    endDateRange: string[];
  }>({
    partnerName: [],
    status: [],
    type: [],
    tier: [],
    vertical: [],
    revenueStream: [],
    accountManager: [],
    region: [],
    country: [],
    contractName: [],
    contractStatus: [],
    supportLevel: [],
    startDateRange: [],
    endDateRange: [],
  });

  const SORTABLE_COLUMNS: PartnerColumnKey[] = [
    "partner_name",
    "partner_type",
    "region",
    "partner_tier",
    "support_level",
    "status",
    "country",
    "revenue_stream",
    "partner_potential",
    "registration_date",
    "last_updated",
  ];

  const [sortColumn, setSortColumn] = React.useState<PartnerColumnKey | null>(
    null
  );

  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(
    "asc"
  );

  const { allPartners, filteredPartners, status } = useAppSelector(
    (state) => state.partner_details
  );
  const { partnerTierList } = useAppSelector((state) => state.partner_program);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isNestedRoute = /\/partner-organizations\/\d+/.test(location.pathname);
  const canUsePartnerFilter = useSessionCanAccessModule(
    PM_KEYS.PARTNER_LIST_FILTER
  );
  const partnerFilterReadOnly = useSessionModuleReadOnly(
    PM_KEYS.PARTNER_LIST_FILTER
  );
  const canAccessPartnerConfig = useSessionCanAccessModule(
    PM_KEYS.PARTNER_CONFIGURATIONS
  );
  const canEditPartnerConfig = useSessionCanEditModule(
    PM_KEYS.PARTNER_CONFIGURATIONS
  );
  const showCreatePartnerButton = canAccessPartnerConfig;
  const canOpenPartnerFromList = useSessionCanAccessAnyModule(
    PARTNER_MANAGE_PARTNER_SUBMENU_KEYS
  );

  const openPartnerFromList = (partnerId: number) => {
    if (!canOpenPartnerFromList) return;
    const base = `/hid-admin/dashboard/partner-organizations/${partnerId}`;
    navigate(`${base}/dashboard`);
  };

  const canOpenPartnerProgram = useSessionCanAccessModule(
    PM_KEYS.ADMIN_PARTNER_PROGRAM
  );
  const openPartnerBenefitsLog = (partnerId: number) => {
    if (!canOpenPartnerProgram) return;
    navigate(
      `/hid-admin/dashboard/partner-organizations/${partnerId}/partner-program?tab=${PARTNER_PROGRAM_BENEFITS_LOG_TAB}`
    );
  };

  const canExportReports = useSessionCanAccessModule(
    PM_KEYS.PARTNER_REPORTS_EXPORT
  );
  const exportReadOnly = useSessionModuleReadOnly(
    PM_KEYS.PARTNER_REPORTS_EXPORT
  );
  const canAccessIntegrationStatus = useSessionCanAccessModule(
    PM_KEYS.ADMIN_PARTNER_INTEGRATION_STATUS
  );
  const canAccessBenefitActivityLog = useSessionCanAccessModule(
    PM_KEYS.BENEFIT_ACTIVITY_LOG
  );

  const availableColumns = React.useMemo(
    () => getAvailablePartnerColumns(),
    []
  );

  const tierNameById = React.useMemo(
    () => buildPartnerTierNameById(partnerTierList),
    [partnerTierList]
  );

  React.useEffect(() => {
    if (!canAccessIntegrationStatus && activeTab === "integration") {
      setActiveTab("organizations");
    }
  }, [canAccessIntegrationStatus, activeTab]);

  React.useEffect(() => {
    if (!canAccessBenefitActivityLog && activeTab === "benefits") {
      setActiveTab("organizations");
    }
  }, [canAccessBenefitActivityLog, activeTab]);

  React.useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    if (tab === "integration" && canAccessIntegrationStatus) {
      setActiveTab("integration");
    } else if (tab === "benefits" && canAccessBenefitActivityLog) {
      setActiveTab("benefits");
    }
  }, [
    location.search,
    canAccessIntegrationStatus,
    canAccessBenefitActivityLog,
  ]);

  React.useEffect(() => {
    dispatch(getAllPartnersSagaAction());
    if (partnerTierList.length === 0) {
      dispatch(getPartnerTierListSagaAction(0));
    }

    if (location.state) {
      if (location.state.organizationStatusFilter) {
        const statusFilter = location.state.organizationStatusFilter;

        setPartnerFilter((prev) => ({
          ...prev,
          status: [statusFilter],
        }));
      }

      if (location.state.revenueStreamFilter) {
        const revenueStreamFilter = location.state.revenueStreamFilter;

        setPartnerFilter((prev) => ({
          ...prev,
          revenueStream: [revenueStreamFilter],
        }));
      }

      if (location.state.partnerTierFilter) {
        const partnerTierFilter = location.state.partnerTierFilter;

        const selectedTier = partnerTierList.find(
          (item) =>
            item.tier_name.toLowerCase() === partnerTierFilter.toLowerCase()
        );

        setPartnerFilter((prev) => ({
          ...prev,
          tier: selectedTier ? [String(selectedTier.ID)] : [],
        }));
      }
    }
  }, []);

  React.useEffect(() => {
    setVisibleColumns((prev) => {
      const validSelection = prev.filter(isPartnerColumnKey);
      if (validSelection.length > 0) return validSelection;
      return [...DEFAULT_VISIBLE_PARTNER_COLUMNS];
    });
  }, [availableColumns]);

  React.useEffect(() => {
    setCurrentPageNumber(1);
  }, [searchTerm, visibleColumns]);

  React.useEffect(() => {
    const hasFilters = Object.values(partnerFilter).some(
      (arr) => Array.isArray(arr) && arr.length > 0
    );
    if (hasFilters) {
      dispatch(
        getFilteredPartnersSagaAction({
          partner_status: partnerFilter.status.join(","),
          partner_name: partnerFilter.partnerName.join(","),
          partner_type: partnerFilter.type.join(","),
          partner_tier: partnerFilter.tier.join(","),
          partner_vertical: partnerFilter.vertical.join(","),
          revenue_stream: partnerFilter.revenueStream.join(","),
          account_manager: partnerFilter.accountManager.join(","),
          region: partnerFilter.region.join(","),
          country: partnerFilter.country.join(","),
          contract_name: partnerFilter.contractName.join(","),
          contract_status: partnerFilter.contractStatus.join(","),
          support_level: partnerFilter.supportLevel.join(","),
          start_date_range: partnerFilter.startDateRange,
          end_date_range: partnerFilter.endDateRange,
        })
      );
    }
    setCurrentPageNumber(1);
  }, [partnerFilter]);

  const statusCounts = React.useMemo(() => {
    return allPartners.reduce<Record<PartnerDetailType["status"], number>>(
      (acc, partner) => {
        acc[partner.status] = (acc[partner.status] || 0) + 1;
        return acc;
      },
      { Active: 0, Registered: 0, Lapsed: 0, Inactive: 0 }
    );
  }, [allPartners]);

  const applyStatusFilter = (statusValue: string) => {
    setPartnerFilter((prev) => ({
      ...prev,
      status: [statusValue],
    }));
  };

  const tiles = [
    {
      id: "active",
      label: "Active Partners",
      value: statusCounts.Active,
      color: KPI_TILE_COLORS.blue,
      onClick: () => applyStatusFilter("Active"),
    },
    {
      id: "registered",
      label: "Registered",
      value: statusCounts.Registered,
      color: KPI_TILE_COLORS.green,
      onClick: () => applyStatusFilter("Registered"),
    },
    {
      id: "lapsed",
      label: "Lapsed",
      value: statusCounts.Lapsed,
      color: KPI_TILE_COLORS.orange,
      onClick: () => applyStatusFilter("Lapsed"),
    },
    {
      id: "inactive",
      label: "Inactive",
      value: statusCounts.Inactive,
      color: KPI_TILE_COLORS.red,
      onClick: () => applyStatusFilter("Inactive"),
    },
  ];

  const hasActiveFilters = Object.values(partnerFilter).some(
    (arr) => Array.isArray(arr) && arr.length > 0
  );

  const partnerList = hasActiveFilters ? filteredPartners : allPartners;

  const filteredPartnersLocal = React.useMemo(() => {
    const filtered = partnerList.filter((partner) => {
      const matchesSearch =
        searchTerm.length === 0 ||
        partner.partner_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        partner.partner_id.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });

    if (!sortColumn) {
      return filtered;
    }

    return [...filtered].sort((a, b) => {
      if (sortColumn === "registration_date" || sortColumn === "last_updated") {
        const dateA = new Date(
          String(getPartnerCellValue(a, sortColumn) ?? "")
        ).getTime();

        const dateB = new Date(
          String(getPartnerCellValue(b, sortColumn) ?? "")
        ).getTime();

        return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
      }

      const valueA = String(
        getPartnerCellValue(a, sortColumn) ?? ""
      ).toLowerCase();

      const valueB = String(
        getPartnerCellValue(b, sortColumn) ?? ""
      ).toLowerCase();

      return sortDirection === "asc"
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    });
  }, [partnerList, searchTerm, sortDirection]);

  const totalRecords = filteredPartnersLocal.length;

  const handlePageChange = (clickedPageNumber: number | null) => {
    setCurrentPageNumber(clickedPageNumber);
  };

  const onPageSizesClick = (e: any, value: any) => {
    setPageSize(Number(value));
    setCurrentPageNumber(1);
  };

  const paginatedData = filteredPartnersLocal.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );

  const partnerStatus = (statusValue: string) => {
    switch (statusValue) {
      case "Active":
        return (
          <InlineStatus
            label="Active"
            appearance={InlineStatusAppearance.Positive}
            iconType={InlineStatusIconType.Dot}
          />
        );
      case "Lapsed":
        return (
          <InlineStatus
            label="Lapsed"
            appearance={InlineStatusAppearance.Caution}
            iconType={InlineStatusIconType.Dot}
          />
        );
      case "Inactive":
        return (
          <InlineStatus
            label="Inactive"
            appearance={InlineStatusAppearance.Danger}
            iconType={InlineStatusIconType.Dot}
          />
        );
      default:
        return (
          <InlineStatus
            label="Registered"
            appearance={InlineStatusAppearance.Info}
            iconType={InlineStatusIconType.Dot}
          />
        );
    }
  };

  const renderPartnerCell = (partner: PartnerDetailType, columnKey: string) => {
    switch (columnKey) {
      case "partner_name":
        return (
          <span className="partner-name-cell">{partner.partner_name}</span>
        );
      case "status":
        return partnerStatus(partner.status);
      case "registration_date":
      case "last_updated": {
        const dateValue =
          columnKey === "last_updated"
            ? partner.last_updated
            : getPartnerCellValue(partner, columnKey);
        if (!dateValue || dateValue === "0000-00-00") return "";
        return moment(dateValue).isValid()
          ? moment(dateValue).format("DD MMM, YYYY")
          : dateValue;
      }
      case "partner_type": {
        const typeValue = partner.partner_type;
        return Array.isArray(typeValue)
          ? typeValue.join(", ")
          : String(typeValue ?? "");
      }
      default:
        return getPartnerCellValue(partner, columnKey);
    }
  };

  if (isNestedRoute) {
    return <Outlet />;
  }

  const handleColumnSort = (columnKey: PartnerColumnKey) => {
    if (sortColumn !== columnKey) {
      setSortColumn(columnKey);
      setSortDirection("asc");
      return;
    }

    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const getSortIcon = (columnKey: PartnerColumnKey) => {
    if (sortColumn !== columnKey) {
      return <Iconography icon="sort" size={IconographySizes.Small} />;
    }

    return sortDirection === "asc" ? (
      <Iconography icon="chevronUp" size={IconographySizes.Small} />
    ) : (
      <Iconography icon="chevronDown" size={IconographySizes.Small} />
    );
  };

  return (
    <div className="partner-list-container">
      <div className="page-header">
        <h1 className="page-title">Partner Organizations</h1>
        <p className="page-subtitle">
          Manage partner orgs, validation status &amp; integration progress.
        </p>
        <div
          className="page-tabs"
          role="tablist"
          aria-label="Partner organizations views"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "organizations"}
            className={`page-tab${
              activeTab === "organizations" ? " page-tab--active" : ""
            }`}
            onClick={() => setActiveTab("organizations")}
          >
            Organizations
          </button>
          {canAccessIntegrationStatus && (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "integration"}
              className={`page-tab${
                activeTab === "integration" ? " page-tab--active" : ""
              }`}
              onClick={() => setActiveTab("integration")}
            >
              Integration Status
            </button>
          )}
          {canAccessBenefitActivityLog && (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "benefits"}
              className={`page-tab${
                activeTab === "benefits" ? " page-tab--active" : ""
              }`}
              onClick={() => setActiveTab("benefits")}
            >
              Benefit Activity Log
            </button>
          )}
        </div>
      </div>

      {activeTab === "organizations" && (
        <>
          <div className="partner-summary-cards">
            {tiles.map((tile) => (
              <button
                type="button"
                className="partner-summary-card"
                key={tile.id}
                onClick={() => tile.onClick()}
              >
                <span className="partner-summary-card__label">
                  {tile.label}
                </span>
                <span className="partner-summary-card__footer">
                  <span className="partner-summary-card__value">
                    {tile.value}
                  </span>
                  <span
                    className="partner-summary-card__dot"
                    style={{ backgroundColor: tile.color }}
                    aria-hidden
                  />
                </span>
              </button>
            ))}
          </div>

          <div className="partner-toolbar">
            <div className="search-input-filter-wrapper">
              <div className="left">
                <SearchInput
                  theme="dark"
                  id="searchPartner"
                  name="searchPartner"
                  placeholder="Search Partner by Name or ID..."
                  isDisplayClearIcon={true}
                  value={searchTerm}
                  onChange={(event: any) =>
                    setSearchTerm(event.currentTarget.value)
                  }
                  onSearch={(event, value) => {
                    setSearchTerm(value);
                  }}
                />
                {showCreatePartnerButton && (
                  <button
                    type="button"
                    className="toolbar-link create-link"
                    onClick={() => setCreatePartnerModalOpen(true)}
                    disabled={!canEditPartnerConfig}
                  >
                    Create New Partner
                  </button>
                )}
              </div>
              <div className="center">
                {canUsePartnerFilter && (
                  <button
                    type="button"
                    className={`toolbar-link filter-link${
                      hasActiveFilters ? " toolbar-link--active" : ""
                    }`}
                    onClick={() => setFilterModalOpen(true)}
                    disabled={partnerFilterReadOnly}
                  >
                    <Iconography icon="filter" size={IconographySizes.Small} />
                    <span>Filter</span>
                  </button>
                )}
                <PartnerColumnsDropdown
                  availableColumns={availableColumns}
                  visibleColumns={visibleColumns}
                  onVisibleColumnsChange={setVisibleColumns}
                />
              </div>
              <div className="right">
                {canExportReports && (
                  <Button
                    variant="primary"
                    label="Export"
                    icon={<Iconography icon="arrowUpFromSquare" />}
                    onClick={() => setExportModalOpen(true)}
                    disabled={exportReadOnly}
                  />
                )}
              </div>
            </div>

            {hasActiveFilters && (
              <div className="filter-tag-wrapper">
                {Object.entries(partnerFilter).map(([filterKey, values]) => {
                  if (filterKey === "startDateRange" && values.length) {
                    return (
                      <Tag
                        value={`Start Date: ${values[0]} - ${values[1]}`}
                        appearance={TagAppearance.Blue}
                        isCloseIcon={true}
                        key={filterKey}
                        onCloseClick={() =>
                          setPartnerFilter((prev) => ({
                            ...prev,
                            [filterKey]: [],
                          }))
                        }
                      />
                    );
                  } else if (filterKey === "endDateRange" && values.length) {
                    return (
                      <Tag
                        value={`End Date: ${values[0]} - ${values[1]}`}
                        appearance={TagAppearance.Blue}
                        isCloseIcon={true}
                        key={filterKey}
                        onCloseClick={() =>
                          setPartnerFilter((prev) => ({
                            ...prev,
                            [filterKey]: [],
                          }))
                        }
                      />
                    );
                  } else {
                    return values.map((value) => {
                      let label = value;
                      switch (filterKey) {
                        case "contractStatus":
                          label = `Contract: ${value}`;
                          break;
                        case "tier":
                          label = `Tier: ${tierNameById.get(value) ?? value}`;
                          break;
                        case "supportLevel":
                          label = `Support: ${value}`;
                          break;
                        default:
                          label = value;
                          break;
                      }
                      return (
                        <Tag
                          value={label}
                          appearance={TagAppearance.Blue}
                          isCloseIcon={true}
                          key={`${filterKey}-${value}`}
                          onCloseClick={() =>
                            setPartnerFilter((prev) => ({
                              ...prev,
                              [filterKey]: prev[
                                filterKey as keyof typeof prev
                              ].filter((item) => item !== value),
                            }))
                          }
                        />
                      );
                    });
                  }
                })}
              </div>
            )}
          </div>

          <div className="table-card">
            {status.allPartnersLoading || status.filteredPartnersLoading ? (
              <div className="list-loading">
                <Loader variant={LoaderSizes.large} />
              </div>
            ) : (
              <>
                <div
                  className="table-container"
                  style={
                    visibleColumns.length > 0
                      ? ({
                          ["--partner-col-count" as string]:
                            visibleColumns.length,
                        } as React.CSSProperties)
                      : undefined
                  }
                >
                  {visibleColumns.length > 0 ? (
                    <>
                      <div className="table-header">
                        {visibleColumns.map((columnKey) => (
                          <div
                            className="table-col"
                            key={columnKey}
                            onClick={
                              SORTABLE_COLUMNS.includes(columnKey)
                                ? () => handleColumnSort(columnKey)
                                : undefined
                            }
                            style={{
                              cursor: SORTABLE_COLUMNS.includes(columnKey)
                                ? "pointer"
                                : "default",
                            }}
                          >
                            {formatPartnerColumnLabel(columnKey)}{" "}
                            {SORTABLE_COLUMNS.includes(columnKey) &&
                              getSortIcon(columnKey)}
                          </div>
                        ))}
                      </div>

                      {paginatedData.length > 0 ? (
                        paginatedData.map((partner, index) => (
                          <div
                            className={`table-row${
                              !canOpenPartnerFromList
                                ? " table-row--disabled"
                                : ""
                            }`}
                            key={partner.partner_id}
                            style={index === 0 ? { border: "none" } : {}}
                            onClick={
                              canOpenPartnerFromList
                                ? () => openPartnerFromList(partner.ID)
                                : undefined
                            }
                          >
                            {visibleColumns.map((columnKey) => (
                              <div className="table-col" key={columnKey}>
                                {renderPartnerCell(partner, columnKey)}
                              </div>
                            ))}
                          </div>
                        ))
                      ) : (
                        <div className="no-data">
                          No data available for display.
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="no-data">
                      Select at least one column to display the partner list.
                    </div>
                  )}
                </div>
                <Pagination
                  currentPageNumber={currentPageNumber}
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  onPageNumberClick={handlePageChange}
                  onPageSizesClick={onPageSizesClick}
                  pageSizes={[
                    { label: "20", value: "20" },
                    { label: "50", value: "50" },
                    { label: "100", value: "100" },
                  ]}
                />
              </>
            )}
          </div>
        </>
      )}
      {activeTab === "integration" && canAccessIntegrationStatus && (
        <IntegrationStatusTab
          onOpenPartner={openPartnerFromList}
          canOpenPartner={canOpenPartnerFromList}
        />
      )}
      {activeTab === "benefits" && canAccessBenefitActivityLog && (
        <BenefitActivityLogTab
          onOpenPartner={openPartnerFromList}
          canOpenPartner={canOpenPartnerFromList}
          onViewAllBenefits={openPartnerBenefitsLog}
          canViewAllBenefits={canOpenPartnerProgram}
        />
      )}

      {activeTab === "organizations" && filterModalOpen && (
        <PartnerFilterModal
          partnerFilterProp={partnerFilter}
          setPartnerFilterProp={setPartnerFilter}
          setModalOpen={setFilterModalOpen}
        />
      )}
      {activeTab === "organizations" && exportModalOpen && (
        <ExportReportModal
          partnerFilterProp={partnerFilter}
          setModalOpen={setExportModalOpen}
        />
      )}
      {createPartnerModalOpen && (
        <CreateNewPartnerModal
          open={createPartnerModalOpen}
          onClose={() => setCreatePartnerModalOpen(false)}
          onSuccess={() => dispatch(getAllPartnersSagaAction())}
        />
      )}
    </div>
  );
};

export default PartnerList;
